// @flow
import React from 'react';
import * as yup from 'yup'
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { PAGE_LOGIN } from "../../locales/pages/namespaces";
import {
    FormControl,
    FormHelperText, Grid,
    OutlinedInput, Stack, TextField, Button, Typography, InputAdornment, Link
} from "@mui/material";

import axios from "axios";
import { API } from "../../helpers/constants";
import { getRouteUrl } from "../../helpers/getRouteUrl";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../helpers/errorHandling";
import { enqueueSnackbar } from "notistack";
import useUserData from "../../hooks/user/useUserData";
import useUserAction from "../../hooks/user/useUserAction";
import ArrowDownCircle from "../../assets/images/icons/buttonsIcons/arrowDownCircle";
import EmailIcon from "../../assets/images/icons/login/emailIcon";

import { useHistory, Link as RouterLink } from 'react-router-dom'
import { ROUTE_PAGE_RESET_PASSWORD, ROUTE_PAGE_LOGIN } from "../../routers/routes";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import Divider from "@mui/material/Divider";

const ResetPasswordForm = () => {

    const { t } = useTranslation(PAGE_LOGIN)
    const history = useHistory()
    const { isLoggedIn, loading } = useUserData()
    const { userLogout } = useUserAction()

    const validationSchema = yup.object({
        email: yup
            .string()
            .required(t('required')).email(t('Enter a valid email'))
    })

    const onSubmit = (values) => {
        axios({
            method: 'get',
            headers: { 'Content-Type': 'application/json' },
            url: `${API}/request/new-password?email=${values.email}`
        }).then(response => {
            history.push(getRouteUrl(ROUTE_PAGE_RESET_PASSWORD, {}, {
                recoveryPwdTime: response.data.recoveryPwdTime,
                resetTime: response.data.resetTime,
                resetType: response.data.resetType,
                email: values.email
            }))
        }).catch(error => {
            enqueueSnackbar(handleAxiosErrors(error, t('The email you entered was not found!')), {
                variant: 'error'
            }
            )
            logoutUnauthorizedUser(error, isLoggedIn, userLogout)
        })
    }

    const formik = useFormik({
        initialValues: {
            email: ''
        },
        validationSchema: validationSchema,
        onSubmit: onSubmit,
    })

    return (
        <FormikProvider value={formik}>
            <div style={{ fontFamily: 'IMS' }}>
                <Grid container spacing={1} className="force-ims">
                    <Grid item xs={12}>
                        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                            <Typography className='color-default title-sm' sx={{ m: '27px auto 0 auto' }}>{t("Reset Password")}</Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} sx={{ marginBottom: '20px' }}>
                        <Typography className='p-sm color-secondary'>
                            {t("Fill in your email and you will receive a message to reset your password.")}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <Stack spacing={1}>
                                    <FormControl
                                        variant="standard"
                                        fullWidth
                                        error={
                                            formik.touched["email"] &&
                                            Boolean(formik.errors["email"])
                                        }
                                    >
                                        <OutlinedInput
                                            name="email"
                                            required
                                            error={formik.touched.email && Boolean(formik.errors.email)}
                                            autoComplete="email"
                                            value={formik.values.email}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            fullWidth
                                            placeholder="Email"
                                            id="email"
                                            className='border-neutral-dark-radius'
                                            sx={{ minHeight: 'unset' }}
                                            startAdornment={
                                                <InputAdornment
                                                    position="start"
                                                    sx={{ bgColor: '#FFFFFF' }}>
                                                    <EmailIcon />
                                                </InputAdornment>
                                            }
                                            inputProps={{
                                                style: {
                                                    fontSize: '14px',
                                                    padding: '10px',
                                                    minHeight: 'unset'
                                                },
                                            }}
                                        />
                                        {formik.touched.email && formik.errors.email && (
                                            <FormHelperText error id="standard-weight-helper-text-email-login">
                                                {formik.errors.email}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} sx={{ margin: '21px auto' }}>
                                <Typography className='p-sm color-secondary'>
                                    {t('Do not forgot to check SPAM box.')}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>



                                <Button
                                    disableElevation
                                    disabled={loading}
                                    fullWidth
                                    type="submit"
                                    sx={{ p: '14px 0', marginBottom:'32px' }}
                                    size={"small"}
                                    variant={"contained"}
                                    className='button-primary'
                                    onClick={formik.submitForm}
                                >
                                    {t('Send')}
                                </Button>

                                <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{
                                        borderTop: '1px solid #eaeef7;',
                                        marginBottom: '16px'
                                    }}
                                />

                                <Typography variant="h5" sx={{ margin: '10px 0 30px 0', display:'block' }} component="span">
                                    <Link
                                        component={RouterLink}
                                        variant="h5"
                                        to={getRouteUrl(ROUTE_PAGE_LOGIN)}
                                        underline="hover"
                                        className='color-default p-sm flex-center'
                                        sx={{
                                            'svg':{marginRight:'10px'}
                                        }}
                                    >
                                        <ArrowBackIosNewIcon fontSize="small" sx={{  marginRight: '18px', width: '10px'}} /> {t('Return')}
                                    </Link>
                                </Typography>




                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </div>
        </FormikProvider>
    )
}

export default ResetPasswordForm